import {Injectable, OnDestroy} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {ActivatedRoute} from '@angular/router';
import {Subject} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  retryWhen,
  delay,
} from 'rxjs/operators';
import {HttpResponse} from '@angular/common/http';
import {BaseService} from '@shared/services/base.service';

const SUFFIX = ' - One ORM';
const DESCRIPTION_TAG = 'description';

@Injectable({
  providedIn: 'root'
})
export class TitleService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly titleEndpoint = '/object-identity';
  private readonly opEventEndpointSuffix = '/op-event';
  private readonly issueEndpointSuffix = '/issue';

  constructor(
    private readonly title: Title,
    private readonly meta: Meta,
    private readonly activatedRoute: ActivatedRoute,
    private readonly httpClient: BaseService
    ) {
  }

  updateTitleOnParamChanges(): void {
    this.activatedRoute.firstChild?.params.pipe(
      filter(params => !!params),
      map(params => ({
        riskCaseId: params['risk-case-id'],
        endpointSuffix: params['case-type'] === 'event' ? this.opEventEndpointSuffix : this.issueEndpointSuffix
      })),
      distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      switchMap(({riskCaseId, endpointSuffix}) => {
        return this.httpClient.get(this.titleEndpoint + endpointSuffix + '/' + riskCaseId);
      }),
      map((response: HttpResponse<{title: string, description?: string}>) => {
        const {title, description} = response.body;
        if (title === null) {
          throw title;
        }
        return {
          title,
          description
        };
      }),
      retryWhen(errors => errors.pipe(
        delay(5000)
      )),
      takeUntil(this.destroy$)
    ).subscribe(({title, description}) => {
      this.title.setTitle(title + SUFFIX);

      if (!!description) {
        this.meta.updateTag({
          name: DESCRIPTION_TAG,
          content: description
        });
      } else {
        this.meta.removeTag(`name='${DESCRIPTION_TAG}'`);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
